import Eyebrow from "../components/Eyebrow";
import Breadcrumbs from "../components/Breadcrumbs";
import CTAButton from "../components/CTAButton";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { SITE } from "../lib/site";
import { dorisPortrait } from "../lib/assets";

const PRINCIPLES = [
  {
    title: "Understand before recommending",
    body: "Every recommendation begins with the person: needs, personality, routines, preferences, location, family circumstances, and budget considerations.",
  },
  {
    title: "Curate rather than list",
    body: "Families don't need a longer list. They need a shorter one that has been thought through on their behalf.",
  },
  {
    title: "Be transparent about how the service is paid",
    body: "Guidance is complimentary to families. CareCraft is compensated by the care community when a placement is made, and that is always said plainly.",
  },
  {
    title: "Keep families informed",
    body: "Families should never be left wondering what comes next. Clarity is part of the care.",
  },
  {
    title: "The final decision belongs to the family",
    body: "CareCraft helps make choices clearer. The choice itself, and its timing, always remains with the family.",
  },
];

export default function About() {
  usePageMeta({
    title: "About CareCraft: Care That Begins With Listening",
    description:
      "Care that begins with listening. CareCraft, founded by Doris Laxamana-Rodriguez, serves families and the community navigating senior care in Los Angeles: understanding the person first, then helping the family find the care that fits.",
    path: "/about",
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "About" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>About CareCraft</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.6rem]">
              <KineticLines lines={[<>Care that begins</>, <>with listening.</>]} />
            </h1>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite pb-20 md:pb-28">
        <div className="mx-auto grid max-w-site gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="mx-auto max-w-[420px]">
                <div className="overflow-hidden rounded-full border border-inkline shadow-[0_20px_50px_rgba(20,46,40,0.15)]">
                  <img
                    src={dorisPortrait}
                    alt="Doris, founder of CareCraft"
                    data-testid="about-doris-photo"
                    className="aspect-square w-full object-cover"
                  />
                </div>
                <p className="mt-6 text-center">
                  <span className="block font-serif text-xl font-medium text-forest">
                    {SITE.founder}
                  </span>
                  <span className="mt-1 block text-[0.8rem] font-medium uppercase tracking-[0.14em] text-charcoal/55">
                    Founder, CareCraft
                  </span>
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl">
                A commitment to families and community
              </h2>
              <p className="mt-6 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                CareCraft grew from Doris's desire to serve families navigating senior care. She saw
                an opportunity to help families approach important care decisions with greater
                clarity and a better understanding of the options available.
              </p>
              <p className="mt-5 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                Her approach begins with listening. Doris takes time to understand the person, their
                circumstances and what matters to the family, then helps them understand their
                options and consider care that may fit.
              </p>
              <figure className="mt-12 border-l-2 border-clay pl-6 md:pl-8">
                <blockquote className="max-w-xl font-serif text-2xl italic leading-snug text-forest md:text-[1.7rem]">
                  “Every family deserves someone who will listen, understand what matters, and help
                  them find their way through the choices ahead.”
                </blockquote>
                <figcaption className="mt-4 text-[0.8rem] font-semibold uppercase tracking-[0.16em] text-charcoal/55">
                  — Doris
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow>Why CareCraft exists</Eyebrow>
                <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl">
                  Understand the person first, then help the family find the care that fits.
                </h2>
                <p className="mt-6 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                  There are many senior care options in Los Angeles. CareCraft exists to help families
                  approach important care decisions with greater clarity, and to help narrow the
                  possibilities to places worth considering.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl">
                  What "Caring is our Craft." means in practice
                </h2>
                <p className="mt-6 max-w-2xl text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                  For CareCraft, caring is reflected in how families are treated throughout the
                  process. It means listening before recommending, being thoughtful about the options
                  we put forward, answering questions honestly, and remembering that every placement
                  decision affects a person, a family, and a life.
                </p>
                <ol className="mt-9 space-y-0 divide-y divide-inkline border-y border-inkline">
                  {PRINCIPLES.map((p, i) => (
                    <li key={p.title} className="grid gap-2 py-6 md:grid-cols-12 md:gap-6">
                      <p className="font-serif text-lg text-clay md:col-span-2" aria-hidden="true">
                        {String(i + 1).padStart(2, "0")}
                      </p>
                      <h3 className="font-serif text-xl font-medium text-forest md:col-span-4">
                        {p.title}
                      </h3>
                      <p className="leading-[1.65] text-charcoal/80 md:col-span-6">{p.body}</p>
                    </li>
                  ))}
                </ol>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="grain bg-forest py-20 text-ivory md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
              <div>
                <h2 className="max-w-xl font-serif text-3xl font-medium leading-tight md:text-4xl">
                  You'll know who you're calling.
                </h2>
                <p className="mt-4 max-w-xl text-ivory/80">
                  Our guidance is complimentary to families. Start by telling us what's happening.
                </p>
              </div>
              <CTAButton to="/contact" variant="ivory" testId="about-final-cta">
                Talk With Doris
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
